import { useStore } from '@nanostores/react';
import { useSearchParams } from '@remix-run/react';
import React, { useEffect, useRef, useState, type RefObject } from 'react';
import { ClientOnly } from 'remix-utils/client-only';
import { useStickToBottom, type ScrollToBottom } from 'use-stick-to-bottom';
import { FooterHomepage } from './FooterHomepage';
import { Messages } from './Messages.client';
import { Prompt } from './Prompt';
import { BoltNewAnnouncement } from './announcements/BoltNewAnnouncement.client';
import { useInfiniteChatScroll } from './utils/useInfiniteChatScroll';
import { IntegrationHomeButton } from '~/components/IntegrationHomeButton';
import { CompatibilityDialog } from '~/components/dialogs/compatibility-dialog.client';
import { ReferredWelcomeDialog } from '~/components/dialogs/referred-welcome';
import { SurveyDialog } from '~/components/dialogs/survey-dialog';
import { Menu } from '~/components/sidebar/Menu.client';
import { ErrorBoundary } from '~/components/ui/ErrorBoundary';
import { LightRaysCloud } from '~/components/ui/LightRaysCloud.client';
import { PricingOverlay } from '~/components/ui/pricing/PricingOverlay.client';
import type { PreviewRef } from '~/components/workbench/Preview';
import { Workbench } from '~/components/workbench/Workbench.client';
import type { PromptMode } from '~/lib/prompts/types';
import { chatStore } from '~/lib/stores/chat';
import { compatibleEnvStore } from '~/lib/stores/compatibility';
import { SHOW_PRICING_OVERLAY_PARAM } from '~/lib/stores/pricing/config';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { surveyStore } from '~/lib/stores/survey';
import { userStore } from '~/lib/stores/user';
import { PricingPageOpenedTrigger } from '~/types/analytics';
import type { FileAttachment, FileSelection, Message } from '~/types/chat';
import { classNames } from '~/utils/classNames';
import { devPrompts } from '~/utils/landingPage';
import { ASYNC_NOOP, NOOP } from '~/utils/noops';

import styles from './BaseChat.module.scss';

interface BaseChatProps {
  hidden?: boolean;
  textareaRef?: RefObject<HTMLTextAreaElement>;
  showChat?: boolean;
  chatStarted?: boolean;
  isDragging?: boolean;
  isStreaming?: boolean;
  promptDisabled?: boolean;
  messages?: Message[];
  previewRef?: RefObject<PreviewRef>;
  enhancingPrompt?: boolean;
  promptEnhanced?: boolean;
  input?: string;
  fileSelections?: FileSelection[];
  attachments?: FileAttachment[];
  mode?: PromptMode;
  loadMoreMessages?: () => Promise<void>;
  onModeChange?: (mode: PromptMode) => void;
  onRollback?: (message: Message) => void;
  acceptSupabaseChange?: (changeId: string) => Promise<void>;
  discardSupabaseChange?: (changeId: string, removeMessage?: boolean) => Promise<void>;
  handleDrop?: (event: React.DragEvent<HTMLDivElement>) => Promise<void>;
  login?: () => true | Promise<boolean>;
  onUploadFile?: (file: File) => Promise<void> | void;
  onImportFigma?: () => void;
  onImportGithub?: () => void;
  onRemoveFileSelection?: (index: number) => void;
  onRemoveAttachment?: (attachment: FileAttachment) => void;
  handleStop?: () => void;
  sendMessage?: (messageInput?: string) => void;
  typeMessage?: (message: string) => Promise<boolean>;
  handleInputChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  enhancePrompt?: () => void;
  resubmit?: (message: Message) => void;
  scrollToBottom?: ScrollToBottom;
}

export const BaseChat = React.forwardRef<HTMLDivElement, BaseChatProps>(
  (
    {
      textareaRef,
      hidden,
      showChat = true,
      chatStarted = false,
      isStreaming = false,
      isDragging = false,
      enhancingPrompt = false,
      promptEnhanced = false,
      promptDisabled = false,
      previewRef,
      messages,
      input = '',
      fileSelections = [],
      attachments = [],
      mode,
      loadMoreMessages = NOOP,
      onModeChange,
      onRollback,
      acceptSupabaseChange,
      discardSupabaseChange,
      handleDrop,
      login,
      onUploadFile,
      onImportFigma,
      onImportGithub,
      onRemoveFileSelection,
      onRemoveAttachment,
      sendMessage,
      typeMessage: _typeMessage,
      handleInputChange,
      enhancePrompt,
      handleStop,
      resubmit,
    },
    ref,
  ) => {
    const [messageBoxHeight, setMessageBoxHeight] = useState(0);
    const [searchParams] = useSearchParams();

    const showPricingOverlay = useStore(pricingStore.showPricingOverlay);
    const showAccountSelector = useStore(userStore.getShowAccountSelector());

    const isLoadingPreviousMessages = useStore(chatStore.isLoadingPreviousMessages);

    const user = useStore(userStore.user);
    const loggedIn = useStore(userStore.loggedIn);
    const isReadOnly = useStore(chatStore.isReadOnly);

    const showSurvey = useStore(surveyStore.showSurvey);
    const showCompatabilityWarning = useStore(compatibleEnvStore.shouldShow);
    const canSkipSurvey = useStore(surveyStore.canSkipSurvey);

    const { scrollRef, contentRef, scrollToBottom, isAtBottom } = useStickToBottom({
      initial: {
        stiffness: 0.1,
      },
    });

    useInfiniteChatScroll({
      scrollRef,
      loadMoreMessages,
      disabled: isLoadingPreviousMessages || isStreaming,
      messages,
    });

    const messageBoxRef = useRef<HTMLDivElement>(null);
    const lastUserMessageIdRef = useRef<string>();

    useEffect(() => {
      if (!messageBoxRef?.current) {
        return undefined;
      }

      const observer = new ResizeObserver(([entry]) => {
        setMessageBoxHeight(entry.contentRect.height);
      });

      observer.observe(messageBoxRef.current);

      return () => observer.disconnect();
    }, []);

    const lastUserMessage = messages?.findLast(({ type, role }) => type === 'chat' && role === 'user');

    useEffect(() => {
      if (searchParams.has(SHOW_PRICING_OVERLAY_PARAM)) {
        pricingStore.openPricingOverlay(PricingPageOpenedTrigger.QueryParam);
      }
    }, []);

    useEffect(() => {
      if (lastUserMessageIdRef.current) {
        scrollToBottom({
          animation: { mass: 1 },
          ignoreEscapes: true,
        });
      }

      lastUserMessageIdRef.current = lastUserMessage?.id;
    }, [lastUserMessage?.id]);

    return (
      <ErrorBoundary>
        <div
          ref={ref}
          className={classNames(
            styles.BaseChat,
            'relative flex h-full w-full overflow-hidden bg-bolt-elements-background-depth-1',
          )}
          aria-hidden={hidden}
          data-chat-visible={showChat}
        >
          <ClientOnly>{() => <ReferredWelcomeDialog />}</ClientOnly>

          {chatStarted && !import.meta.env.VITE_DISABLE_INTERRUPTS && (
            <ClientOnly>{() => <CompatibilityDialog />}</ClientOnly>
          )}

          <ClientOnly>
            {() =>
              loggedIn &&
              !showCompatabilityWarning &&
              user?.surveys?.onboarding !== 'finished' &&
              !import.meta.env.VITE_DISABLE_INTERRUPTS ? (
                <SurveyDialog open={showSurvey} allowSkip={canSkipSurvey} />
              ) : null
            }
          </ClientOnly>

          <ClientOnly>{() => (loggedIn && user ? <Menu user={user} /> : null)}</ClientOnly>

          <ClientOnly>{() => (showPricingOverlay && showAccountSelector ? <PricingOverlay /> : null)}</ClientOnly>

          <div
            ref={scrollRef}
            className={classNames('flex overflow-auto size-full overscroll-contain', {
              'flex-col relative': !chatStarted,
            })}
          >
            {!chatStarted && (
              <ClientOnly>{() => <LightRaysCloud className="absolute inset-0 w-full h-full" />}</ClientOnly>
            )}

            <ClientOnly>{() => !chatStarted && <BoltNewAnnouncement />}</ClientOnly>

            <div className={classNames(styles.Chat, 'selection-accent flex flex-col flex-grow relative z-9')}>
              {!chatStarted && (
                <div className="flex flex-col mt-[220px] mx-[var(--chat-padding)]">
                  <h1 className="text-[44px] text-center font-semibold text-bolt-elements-textPrimary tracking-tight">
                    What should we build today?
                  </h1>
                  <p className="mb-6 text-center text-bolt-ds-textTertiary max-w-[400px] mx-auto">
                    <span>Create stunning apps & websites by chatting with AI.</span>
                  </p>
                </div>
              )}
              <div
                className={classNames({
                  'h-full flex flex-col': chatStarted,
                })}
              >
                <ClientOnly>
                  {() => {
                    return chatStarted ? (
                      <Messages
                        ref={contentRef}
                        messageBoxHeight={messageBoxHeight}
                        className="flex flex-col gap-6 w-full flex-1 max-w-chat py-4 mx-auto z-1 text-sm"
                        messages={messages}
                        isAtBottom={isAtBottom}
                        sendMessage={sendMessage}
                        onRollback={onRollback}
                        resubmit={resubmit}
                        acceptSupabaseChange={acceptSupabaseChange ?? ASYNC_NOOP}
                        discardSupabaseChange={discardSupabaseChange ?? ASYNC_NOOP}
                        isStreaming={isStreaming}
                        scrollToBottom={scrollToBottom}
                        user={user}
                      />
                    ) : null;
                  }}
                </ClientOnly>
                <Prompt
                  ref={messageBoxRef}
                  isReadOnly={isReadOnly}
                  textareaRef={textareaRef}
                  chatStarted={chatStarted}
                  isDragging={isDragging}
                  isStreaming={isStreaming}
                  disableInput={promptDisabled}
                  enhancingPrompt={enhancingPrompt}
                  promptEnhanced={promptEnhanced}
                  input={input}
                  fileSelections={fileSelections}
                  attachments={attachments}
                  handleDrop={handleDrop}
                  onUploadFile={onUploadFile}
                  onRemoveFileSelection={onRemoveFileSelection}
                  onRemoveAttachment={onRemoveAttachment}
                  handleStop={handleStop}
                  sendMessage={sendMessage}
                  handleInputChange={handleInputChange}
                  enhancePrompt={enhancePrompt}
                  mode={mode}
                  onModeChange={onModeChange}
                />
              </div>

              <ClientOnly>
                {() => {
                  return (
                    !chatStarted && (
                      <div className="relative flex flex-col gap-9 w-full max-w-4xl mx-auto flex justify-center mt-2 2xl:max-w-[70rem]">
                        {import.meta.env.DEV && (
                          <div className="flex justify-center text-sm gap-2">
                            {devPrompts.map((prompt, index) => {
                              return (
                                <button
                                  key={index}
                                  className="border border-bolt-elements-borderColor rounded-full bg-gray-50 hover:bg-gray-100 dark:bg-gray-950 dark:hover:bg-gray-900 text-bolt-elements-textSecondary hover:text-bolt-elements-textPrimary px-3 py-1 text-xs transition-theme"
                                  onClick={() => sendMessage?.(prompt)}
                                >
                                  {prompt}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        <div className="flex text-sm items-center align-baseline gap-4 justify-center">
                          <p className="text-center text-bolt-elements-textSecondary">or import from</p>
                          <div className="flex justify-center gap-3">
                            <IntegrationHomeButton
                              title="Figma"
                              icon="i-bolt:logos-figma"
                              onClick={() => onImportFigma?.()}
                            />

                            <IntegrationHomeButton
                              title="GitHub"
                              icon="i-bx:bxl-github"
                              onClick={() => onImportGithub?.()}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  );
                }}
              </ClientOnly>
            </div>

            <ClientOnly>{() => <Workbench login={login} open={chatStarted} previewRef={previewRef} />}</ClientOnly>

            <ClientOnly>
              {() => {
                return !chatStarted && <FooterHomepage />;
              }}
            </ClientOnly>
          </div>
        </div>
      </ErrorBoundary>
    );
  },
);
